import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowUpRight } from 'lucide-react';
import { POExtractionPanel } from './POExtractionPanel';
import { POLineItemsEditor } from './POLineItemsEditor';
import { EntityCustomFields } from '../shared/EntityCustomFields';
import { DatePicker } from '../shared/DatePicker';
import { useAuth } from '../../context/AuthContext';

// Create + Edit dialog for Content POs — used from the combined Purchase Orders table.
export const ContentPOFormDialog = ({ open, onOpenChange, editingPo, form, setForm, onSubmit, onExtracted, saving, workloadTypeOptions = [], onSchemaChanged }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const workloadOptions = workloadTypeOptions.length > 0
    ? workloadTypeOptions
    : [{ value: 'Content Development', label: 'Content Development' }];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="content-po-edit-dialog">
        <DialogHeader>
          <DialogTitle>{editingPo ? 'Edit Content PO' : 'New Content PO'}</DialogTitle>
          <DialogDescription>Microsoft-issued PO funding content build/refresh activities</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Workload Type</Label>
            <Select value={form.workload_type || workloadOptions[0]?.value} onValueChange={(v) => setForm({ ...form, workload_type: v })}>
              <SelectTrigger data-testid="content-po-workload-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {workloadOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!editingPo && (
            <POExtractionPanel poType="content" onExtracted={onExtracted} initialFilename={form.document_filename} />
          )}
          <div>
            <Label>PO Number</Label>
            <Input value={form.po_number} onChange={e => setForm({ ...form, po_number: e.target.value })} placeholder="e.g. MS-CONTENT-2026-001" data-testid="content-po-edit-number-input" />
          </div>
          <div>
            <Label>Short Title</Label>
            <Input value={form.short_title} onChange={e => setForm({ ...form, short_title: e.target.value })} placeholder="e.g. Fabric FY26 Build" data-testid="content-po-edit-short-title-input" />
          </div>
          <div>
            <Label>Total Amount ($)</Label>
            <Input type="number" value={form.total_amount} onChange={e => setForm({ ...form, total_amount: e.target.value })} data-testid="content-po-edit-amount-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Issue Date</Label>
              <DatePicker value={form.issue_date} onChange={(v) => setForm({ ...form, issue_date: v })} placeholder="Select issue date" testId="content-po-edit-issue-date-input" />
            </div>
            <div>
              <Label>Expiry Date</Label>
              <DatePicker value={form.expiry_date} onChange={(v) => setForm({ ...form, expiry_date: v })} placeholder="Select expiry date" testId="content-po-edit-expiry-date-input" />
            </div>
          </div>
          <div>
            <Label>Microsoft Owner</Label>
            <Input value={form.ms_owner} onChange={e => setForm({ ...form, ms_owner: e.target.value })} placeholder="e.g. Jane Doe (Microsoft)" data-testid="content-po-edit-ms-owner-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="e.g. Fabric content build FY26" data-testid="content-po-edit-description-input" />
          </div>
          <POLineItemsEditor lineItems={form.line_items} onChange={(items) => setForm({ ...form, line_items: items })} />

          <div className="border-t pt-4">
            <EntityCustomFields
              entityType="content_po"
              values={form.custom_fields || {}}
              onChange={(v) => setForm({ ...form, custom_fields: v })}
              isSuperAdmin={user?.role === 'superadmin'}
              currentUser={user}
              onSchemaChanged={onSchemaChanged}
              testPrefix="content-po-custom-field"
            />
          </div>

          {editingPo && editingPo.linked_items?.length > 0 && (
            <div>
              <Label className="text-sm">Linked Content Items ({editingPo.linked_items.length})</Label>
              <div className="space-y-1.5 max-h-40 overflow-y-auto mt-1.5" data-testid="content-po-edit-linked-items">
                {editingPo.linked_items.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => { onOpenChange(false); navigate(`/content-items/${item.id}`); }}
                    className="w-full flex items-center justify-between text-sm border rounded-lg px-3 py-2 hover:bg-muted/50 transition-colors"
                    data-testid={`content-po-edit-linked-item-${item.id}`}
                  >
                    <span className="truncate">{item.title}</span>
                    <span className="flex items-center gap-2 text-muted-foreground flex-shrink-0">
                      ${item.po_amount?.toLocaleString() || 0}<ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={saving} data-testid="content-po-edit-save-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">
            {saving ? 'Saving...' : editingPo ? 'Save Changes' : 'Create Content PO'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContentPOFormDialog;
